import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Info } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Eyebrow } from "@/components/site/motion";
import { PhotoFrame } from "@/components/site/Placeholders";
import { Btn } from "@/components/site/Buttons";
import { Icon } from "@/components/site/Icon";
import { Notice112 } from "@/components/site/Notice112";
import { SERVICES, requestLink, PHONE, PHONE_HREF } from "@/data/site";
import NotFound from "./NotFound";

const List = ({ title, items }) => (
  <div>
    <h3 className="text-xs font-semibold uppercase tracking-[0.24em] text-gold-ink">{title}</h3>
    <ul className="mt-6 divide-y divide-navy/10 border-y border-navy/10">
      {items.map((it, i) => (
        <Reveal as="li" key={it} delay={i * 0.04} y={12} className="flex items-start gap-4 py-4 text-base text-navy">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gold/60"><Check className="h-3.5 w-3.5 text-gold-ink" /></span>
          {it}
        </Reveal>
      ))}
    </ul>
  </div>
);

export default function ServizioDettaglio() {
  const { slug } = useParams();
  const idx = SERVICES.findIndex((s) => s.slug === slug);
  if (idx < 0) return <NotFound />;
  const s = SERVICES[idx];
  const next = SERVICES[(idx + 1) % SERVICES.length];

  return (
    <div data-testid={`service-page-${s.slug}`}>
      <PageHero key={s.slug} eyebrow={`Servizio 0${idx + 1} · Servizi`} lines={[s.title]} intro={s.intro}>
        <div className="mt-10 flex flex-col gap-4 sm:flex-row">
          <Btn to={requestLink(s.need)} testId="service-hero-cta-btn">{s.cta}</Btn>
          <Btn href={PHONE_HREF} variant="ghost" testId="service-hero-phone-link">Chiama {PHONE}</Btn>
        </div>
      </PageHero>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.15fr] lg:gap-20">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <Reveal>
              <PhotoFrame src={s.photo} fit={s.photoFit} position={s.photoPosition || "center"} label={s.title} className={`rounded-[28px] ${s.photo ? "aspect-[4/3]" : "min-h-[300px] md:min-h-[440px]"}`} testId="service-photo" />
            </Reveal>
            <Reveal delay={0.1} className="mt-6 flex items-center gap-4 rounded-2xl bg-mist p-5">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-navy"><Icon name={s.icon} className="h-5 w-5 text-gold" /></span>
              <p className="text-sm leading-relaxed text-slate2">Ogni servizio è pianificato dalla Centrale Operativa e svolto da partner qualificati della rete Nexus.</p>
            </Reveal>
          </div>
          <div className="space-y-14">
            <List title={s.listTitle} items={s.items} />
            {s.secondItems && <List title={s.secondTitle} items={s.secondItems} />}
            {s.highlight && (
              <Reveal className="rounded-2xl border-l-4 border-gold bg-mist p-6 text-base font-medium leading-relaxed text-navy md:p-8">{s.highlight}</Reveal>
            )}
            {s.note && (
              <Reveal className="flex items-start gap-3 text-sm leading-relaxed text-slate2">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-gold-ink" /> {s.note}
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <section data-testid="service-key-message" className="grain relative overflow-hidden bg-navy py-20 text-white md:py-28">
        <div className="pointer-events-none absolute -right-32 -top-32 h-[460px] w-[460px] rounded-full border border-gold/25" />
        <div className="relative mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal><Eyebrow light>Il nostro impegno</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <blockquote className="mt-8 text-2xl font-light leading-snug md:text-4xl">“{s.message}”</blockquote>
          </Reveal>
          <Reveal delay={0.2} className="mt-12">
            <Btn to={requestLink(s.need)} variant="gold" testId="service-message-cta-btn">{s.cta}</Btn>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Notice112 />
          <div className="mt-14 flex flex-col justify-between gap-6 border-t border-navy/10 pt-10 sm:flex-row sm:items-center">
            <Link to="/servizi" data-testid="service-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-slate2 hover:text-navy">
              <ArrowLeft className="h-4 w-4" /> Tutti i servizi
            </Link>
            <Link to={`/servizi/${next.slug}`} data-testid="service-next-link" className="group text-right">
              <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-gold-ink">Servizio successivo</span>
              <span className="mt-1 inline-flex items-center gap-2 text-lg font-semibold text-navy">
                {next.title} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
